import { useState, useEffect, useMemo, useRef, FC, useCallback } from 'react';
import { Card, CardBody } from 'reactstrap';
import { debounce } from 'lodash';
import { useTableHeaderContext } from 'shared/components/tableHeader/TableHeaderContext';
import {
  useGovernanceTags,
  useGovernanceTagsPaginated,
  useGovernanceTagsForExport,
} from '../../hooks/useGovernanceTags';
import { useScrollSync } from 'shared/hooks/customHooks/useScrollSync';
import Paper from '@mui/material/Paper';
import GovernanceTagsModal from '../addEditModal/GovernanceTagsModal';
import GovernanceTagsTableView from './GovernanceTagsTableView';
import {
  ResourceData,
  ColumnWidthInfo,
  ResourceFilter,
  GovernanceTagParams,
  BulkDeleteResult,
  SortParams,
  FilterParams,
} from '../../types';
import { COLUMN_NAMES } from '../../constants/columnNames';
import { ROWS_OPTIONS, FIXED_COLUMNS_FOR_RESIZING, COLUMNS_NAMES_AND_WIDTHS } from '../../constants/tableOptions';
import { downloadResourcesAsCSV } from '../../utils';
import GovernanceTagsFilterSidebar from './FilterSidebar';
import GovernanceTagsTableHeader from './TableHeader';
import toast from 'shared/components/andtComponents/Toast';
import { numberWithCommas } from 'shared/utils/strUtil';

import styles from './GovernanceTagsTableController.module.scss';

const GovernanceTagsTableController: FC = () => {
  const { searchText, filtersOpen, setFiltersOpen } = useTableHeaderContext();

  const {
    hiddenColumnNames,
    addGovernanceTags,
    updateGovernanceTags,
    deleteGovernanceTags,
    getActiveFiltersCount,
    filterParams,
    metrics,
  } = useGovernanceTags();

  const [sortConfig, setSortConfig] = useState<SortParams[]>([
    { columnName: COLUMN_NAMES.RESOURCE_COST, direction: 'desc' },
  ]);

  const [showTagged, setShowTagged] = useState(true);
  const [showUntagged, setShowUntagged] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState<ResourceData | null>(null);
  const [selectedResources, setSelectedResources] = useState<(string | number)[]>([]);
  const [expandedRowIds, setExpandedRowIds] = useState<(string | number)[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState({ label: '20 rows (Default)', value: 20 });
  const [openRowsDropdown, setOpenRowsDropdown] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [columnsWidth, setColumnsWidth] = useState<ColumnWidthInfo[]>(COLUMNS_NAMES_AND_WIDTHS);
  const [tableFilters, setTableFilters] = useState<FilterParams>({});

  const topScrollRef = useRef<HTMLDivElement>(null);
  const gridContainerRef = useRef<HTMLDivElement>(null);

  useScrollSync({
    sourceRef: topScrollRef,
    targetRef: gridContainerRef,
    targetSelector: '.TableContainer-root',
    direction: 'horizontal',
  });

  const computedResourceFilter = useMemo(() => {
    if (showTagged && showUntagged) {
      return ResourceFilter.ALL;
    } else if (showTagged) {
      return ResourceFilter.TAGGED;
    } else if (showUntagged) {
      return ResourceFilter.UNTAGGED;
    } else {
      return ResourceFilter.ALL;
    }
  }, [showTagged, showUntagged]);

  const combinedFilterParams = useMemo(() => {
    return {
      ...filterParams,
      ...tableFilters,
    };
  }, [filterParams, tableFilters]);

  const queryParams = useMemo(() => {
    return {
      filter: computedResourceFilter,
      searchText,
      page: currentPage,
      pageSize: itemsPerPage.value,
      sortBy: sortConfig,
      ...combinedFilterParams,
    };
  }, [computedResourceFilter, searchText, currentPage, itemsPerPage.value, sortConfig, combinedFilterParams]);

  const exportQueryParams = useMemo(
    () => ({
      filter: computedResourceFilter,
      ...filterParams,
      searchText: searchText || undefined,
      sortBy: sortConfig,
    }),
    [computedResourceFilter, filterParams, searchText, sortConfig],
  );

  const {
    data: paginatedData,
    isLoading: isLoadingResources,
    error,
    invalidatePaginatedResources,
  } = useGovernanceTagsPaginated(queryParams);

  const { refetch: refetchAllResourcesForExport, isLoading: isExportLoading } =
    useGovernanceTagsForExport(exportQueryParams);

  const [previousTotalCount, setPreviousTotalCount] = useState(0);
  const [previousTotalPages, setPreviousTotalPages] = useState(0);

  useEffect(() => {
    if (paginatedData && !isLoadingResources) {
      setPreviousTotalCount(paginatedData.total || 0);
      setPreviousTotalPages(paginatedData.totalPages || 0);
    }
  }, [paginatedData, isLoadingResources]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, computedResourceFilter, filterParams, sortConfig, itemsPerPage, tableFilters]);

  const [isBulkMode, setIsBulkMode] = useState(false);

  const handleOpenModal = (resource: ResourceData) => {
    setSelectedResource(resource);
    setIsBulkMode(false);
    setIsModalOpen(true);
  };

  const handleAddGovernanceTag = (resources: ResourceData[]) => {
    if (resources.length === 1) {
      handleOpenModal(resources[0]);
    } else if (resources.length > 1) {
      const bulkResource: ResourceData = {
        id: 'bulk',
        resourceCost: 0,
        resourceName: `${resources.length} resources`,
        resourceId: 'bulk-operation',
        service: '',
        region: '',
        linkedAccount: '',
        tags: {},
        governanceTags: [],
        lastUpdate: '',
      };
      setSelectedResource(bulkResource);
      setIsBulkMode(true);
      setIsModalOpen(true);
    }
  };

  const handleBulkSaveTags = async (params: GovernanceTagParams) => {
    if (!isBulkMode) {
      const hasExistingTags = selectedResource?.governanceTags && selectedResource.governanceTags.length > 0;
      if (hasExistingTags) {
        await updateGovernanceTags(params);
      } else {
        await addGovernanceTags(params);
      }
    } else {
      const selectedResourcesData = (paginatedData?.resources || []).filter((_, index) =>
        selectedResources.includes(index),
      );
      const bulkParams = {
        ...params,
        resourceIds: selectedResourcesData.map((resource) => resource.resourceId),
      };
      await addGovernanceTags(bulkParams);
    }
    setIsModalOpen(false);
    setIsBulkMode(false);
    setSelectedResources([]);
    invalidatePaginatedResources();
  };

  const handleDownloadCSV = async () => {
    try {
      const { data: allResources } = await refetchAllResourcesForExport();
      downloadResourcesAsCSV(allResources || []);
    } catch {
      toast.error('Failed to export data');
    }
  };

  const handleRemoveGovernanceTag = async (resources: ResourceData[]) => {
    try {
      const resourceIds = resources.map((resource) => resource.resourceId);
      const results: BulkDeleteResult[] = await deleteGovernanceTags(resourceIds);

      const successCount = results.filter((result) => result.success).length;
      const errorCount = results.filter((result) => !result.success).length;

      if (successCount === 0) {
        toast.error('Failed to remove virtual tags');
      } else if (errorCount > 0) {
        toast.warning(`Virtual tags removed from ${successCount} resources, ${errorCount} failed`);
      }

      setSelectedResources([]);
      invalidatePaginatedResources();
    } catch {
      toast.error('Failed to remove virtual tags');
    }
  };

  const handleDeleteGovernanceTagsFromModal = async (resourceId: string): Promise<void> => {
    await deleteGovernanceTags([resourceId]);
    invalidatePaginatedResources();
  };

  const selectedResourcesData = useMemo(() => {
    return (paginatedData?.resources || []).filter((_, index) => selectedResources.includes(index));
  }, [paginatedData?.resources, selectedResources]);

  const getAdditionalSummaryData = useMemo(() => {
    if (selectedResourcesData.length === 0) return null;

    const totalCost = selectedResourcesData.reduce((sum, resource) => {
      const cost = resource.resourceCost || 0;
      return sum + cost;
    }, 0);

    const taggedCount = selectedResourcesData.filter(
      (resource) => resource.governanceTags && resource.governanceTags.length > 0,
    ).length;

    return (
      <>
        <div className={styles.partWrapper}>
          <span className={`${styles.number} ${styles.green}`}>${numberWithCommas(totalCost, 2)}</span>
          Total Cost
        </div>
        <div className={styles.partWrapper}>
          <span className={styles.number}>
            {numberWithCommas(taggedCount, 0)}/{numberWithCommas(selectedResourcesData.length, 0)}
          </span>
          Tagged Resources
        </div>
      </>
    );
  }, [selectedResourcesData]);

  const handleColumnWidthsChange = (newColumnWidths: ColumnWidthInfo[]) => {
    const updatedWidths = columnsWidth.map((col) => {
      if (FIXED_COLUMNS_FOR_RESIZING.includes(col.columnName as COLUMN_NAMES)) {
        return col;
      }
      const updatedCol = newColumnWidths.find((newCol) => newCol.columnName === col.columnName);
      return updatedCol || col;
    });
    setColumnsWidth(updatedWidths);
  };

  const activeFiltersCount = getActiveFiltersCount();

  const handleSortingChange = (newSorting: { columnName: string; direction: 'asc' | 'desc' }[]) => {
    setSortConfig(newSorting);
  };

  const updateTableFilters = useCallback(
    (filters: Array<{ columnName: string; value: string; operation?: string }>) => {
      const tempFilters: FilterParams = {};

      filters.forEach((f) => {
        if (f.value) {
          switch (f.columnName) {
            case COLUMN_NAMES.SERVICE: {
              tempFilters.service = [f.value];
              break;
            }
            case COLUMN_NAMES.REGION: {
              tempFilters.region = [f.value];
              break;
            }
            case COLUMN_NAMES.RESOURCE_NAME: {
              tempFilters.resourceName = [f.value];
              break;
            }
            case COLUMN_NAMES.RESOURCE_ID: {
              tempFilters.resourceid = [f.value];
              break;
            }
            case COLUMN_NAMES.LINKED_ACCOUNT: {
              tempFilters.linkedaccid = [f.value];
              break;
            }
            case COLUMN_NAMES.TAGS: {
              tempFilters.tags = [f.value];
              break;
            }
            case COLUMN_NAMES.GOVERNANCE_TAGS: {
              tempFilters.governancetags = [f.value];
              break;
            }
            case COLUMN_NAMES.RESOURCE_COST: {
              if (f.operation) {
                const filterKey = `resourceCost_${f.operation}`;
                tempFilters[filterKey] = [f.value];
              } else {
                tempFilters.resourceCost = [f.value];
              }
              break;
            }
            case COLUMN_NAMES.LAST_UPDATE: {
              if (f.operation) {
                const filterKey = `lastUpdate_${f.operation}`;
                tempFilters[filterKey] = [f.value];
              } else {
                tempFilters.lastUpdate = [f.value];
              }
              break;
            }
            default:
              break;
          }
        }
      });

      setTableFilters(tempFilters);
    },
    [],
  );

  const debouncedUpdateTableFilters = debounce(updateTableFilters, 800);

  const handleFiltersChange = useCallback(
    (filters: Array<{ columnName: string; value: string; operation?: string }>) => {
      debouncedUpdateTableFilters(filters);
    },
    [debouncedUpdateTableFilters],
  );

  const resources = paginatedData?.resources || [];

  const totalPages = isLoadingResources && previousTotalPages > 0 ? previousTotalPages : paginatedData?.totalPages || 0;
  const totalCount = isLoadingResources && previousTotalCount > 0 ? previousTotalCount : paginatedData?.total || 0;

  const getTotalResourcesLength = useMemo(() => {
    return metrics?.tagCoverage?.denominator?.count || 0;
  }, [metrics]);

  if (error) {
    return <div>Error loading resources. Please try again.</div>;
  }

  return (
    <div className={styles.governanceTagsPageLayout}>
      <div className={styles.mainContentContainer}>
        <Card className={styles.governanceTagsTableContainer}>
          <CardBody>
            <GovernanceTagsTableHeader
              setters={{
                setShowTagged,
                setShowUntagged,
                setFiltersOpen,
                setShowFilters,
                setItemsPerPage,
                setOpenRowsDropdown,
                setCurrentPage,
              }}
              flags={{
                showTagged,
                showUntagged,
                filtersOpen,
                showFilters,
                openRowsDropdown,
                isLoadingResources,
                isExporting: isExportLoading,
              }}
              config={{
                filteredDataLength: totalCount,
                totalResourcesLength: getTotalResourcesLength,
                itemsPerPage,
                rowsOptions: ROWS_OPTIONS,
                filteredData: resources,
              }}
              pagination={{
                currentPage,
                totalPages,
              }}
              handleDownloadCSV={handleDownloadCSV}
              activeFiltersCount={activeFiltersCount}
            />
            <div className={styles.tableWithSidebarContainer}>
              <GovernanceTagsFilterSidebar isOpen={filtersOpen} onClose={() => setFiltersOpen(false)} />
              <div className={styles.tableContent} ref={gridContainerRef}>
                <Paper className={styles.tablePaper}>
                  <GovernanceTagsTableView
                    data={{
                      resources,
                      isLoading: isLoadingResources,
                      selectedResources,
                      expandedRowIds,
                      selectedResourcesData,
                      columnsWidth,
                    }}
                    config={{
                      sortConfig,
                      hiddenColumnNames,
                    }}
                    filterConfig={{
                      showFilters,
                    }}
                    setters={{
                      setExpandedRowIds,
                      onColumnWidthsChange: handleColumnWidthsChange,
                    }}
                    handlers={{
                      onSortingChange: handleSortingChange,
                      onSelectionChange: setSelectedResources,
                      onEdit: handleOpenModal,
                      onAddGovernanceTag: handleAddGovernanceTag,
                      onRemoveGovernanceTag: handleRemoveGovernanceTag,
                      onFiltersChange: handleFiltersChange,
                    }}
                    getters={{
                      getAdditionalSummaryData,
                    }}
                  />
                </Paper>
              </div>
            </div>

            <GovernanceTagsModal
              isOpen={isModalOpen}
              onClose={() => {
                setIsModalOpen(false);
                setIsBulkMode(false);
              }}
              resource={selectedResource}
              onSaveTags={handleBulkSaveTags}
              onDeleteTags={handleDeleteGovernanceTagsFromModal}
            />
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default GovernanceTagsTableController;
